import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    EmailAuthProvider,
    reauthenticateWithCredential,
    multiFactor,
    TotpMultiFactorGenerator,
} from 'firebase/auth';
import { QRCodeSVG } from 'qrcode.react';
import { auth } from '../firebase/firebase';
import { useAuth } from '../context/AuthContext';
import { notify } from '../utils/notifications';
import '../styles/MfaSetup.css';

const TOTP_ISSUER = 'CensusZen';
const FACTOR_DISPLAY_NAME = 'Authenticator app';
const NOT_SIGNED_IN_MESSAGE = 'Still signing in. Try again in a moment.';
const STALE_SESSION_MESSAGE = 'Your session is too old. Sign in again, then set up two-factor.';

/** Errors from the password step: re-authentication, getSession, generateSecret. */
const startMessageFor = (err) => {
    switch (err?.code) {
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
            return 'Password is incorrect.';
        case 'auth/unverified-email':
            return 'Verify your email first.';
        case 'auth/too-many-requests':
            return 'Too many attempts. Wait a few minutes and try again.';
        default:
            return 'Could not start enrollment. Try again.';
    }
};

/** Errors from the code step: assertionForEnrollment and enroll. */
const enrollMessageFor = (err) => {
    switch (err?.code) {
        case 'auth/invalid-verification-code':
            return 'That code did not work. Enter the current code from your authenticator app.';
        case 'auth/requires-recent-login':
            return STALE_SESSION_MESSAGE;
        default:
            return 'Could not finish enrollment. Try again.';
    }
};

/**
 * The user's enrolled second factors, or [] when there is no signed-in user
 * or the SDK object does not support multiFactor (mocked users in tests).
 */
const readEnrolledFactors = () => {
    const u = auth.currentUser;
    if (!u) return [];
    try {
        return multiFactor(u).enrolledFactors ?? [];
    } catch (err) {
        return [];
    }
};

/**
 * C2: TOTP enrollment at /mfa-setup. ProtectedRoute sends site and facility
 * admins here until multiFactor(user).enrolledFactors is non-empty; members
 * reach it from the profile page. Steps:
 *   enrolled: a factor already exists at mount (stale tab or bookmark);
 *             show the enabled state and a Continue button, no password form;
 *   password: re-authenticate (Firebase requires a recent sign-in to enroll),
 *             open a multi-factor session and generate the TOTP secret;
 *   scan:     show the QR code and the manual key, accept a six-digit code,
 *             enroll the factor;
 *   done:     refresh the ID token and the context's mfaEnrolled, go home.
 */
export const MfaSetup = () => {
    const [step, setStep] = useState(() => (readEnrolledFactors().length > 0 ? 'enrolled' : 'password'));
    const [password, setPassword] = useState('');
    const [secret, setSecret] = useState(null);
    const [qrUrl, setQrUrl] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [needsVerifiedEmail, setNeedsVerifiedEmail] = useState(false);
    const [pending, setPending] = useState(false);
    const navigate = useNavigate();
    const { isAdmin, isFacilityAdmin, refreshMfaState, signOut } = useAuth();

    const required = isAdmin || isFacilityAdmin;

    const resetToPassword = () => {
        setStep('password');
        setSecret(null);
        setQrUrl('');
        setCode('');
    };

    const handlePassword = async (e) => {
        e.preventDefault();
        setError('');
        setNeedsVerifiedEmail(false);
        const u = auth.currentUser;
        if (!u) {
            notify.info(NOT_SIGNED_IN_MESSAGE);
            return;
        }
        setPending(true);
        try {
            await reauthenticateWithCredential(u, EmailAuthProvider.credential(u.email, password));
            const session = await multiFactor(u).getSession();
            const totpSecret = await TotpMultiFactorGenerator.generateSecret(session);
            setSecret(totpSecret);
            setQrUrl(totpSecret.generateQrCodeUrl(u.email, TOTP_ISSUER));
            setPassword('');
            setStep('scan');
        } catch (err) {
            if (err?.code === 'auth/unverified-email') {
                setNeedsVerifiedEmail(true);
            }
            setError(startMessageFor(err));
        } finally {
            setPending(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        setError('');
        const u = auth.currentUser;
        if (!u) {
            notify.info(NOT_SIGNED_IN_MESSAGE);
            return;
        }
        if (code.length !== 6) {
            setError('Enter the six-digit code from your authenticator app.');
            return;
        }
        setPending(true);
        try {
            const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
            await multiFactor(u).enroll(assertion, FACTOR_DISPLAY_NAME);
        } catch (err) {
            if (err?.code === 'auth/requires-recent-login') {
                resetToPassword();
            }
            setError(enrollMessageFor(err));
            setPending(false);
            return;
        }
        // The factor is enrolled from here on. A failure below must not read
        // as a failed enrollment; AuthContext re-reads the factors on the next
        // token change anyway.
        setStep('done');
        try {
            await u.getIdToken(true);
            await refreshMfaState();
        } catch (err) {
            // Fall through to navigate; the gate reads the refreshed state.
        }
        setPending(false);
        navigate('/');
    };

    const onCodeChange = (e) => {
        setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
    };

    const errorBlock = error && (
        <div className="error" role="alert">
            {error}
            {needsVerifiedEmail && <Link to="/verify-email">Verify email</Link>}
        </div>
    );

    const footer = (
        <div className="mfa-setup-footer">
            {!required && (
                <button type="button" className="tos-link" onClick={() => navigate('/')} disabled={pending}>
                    Cancel
                </button>
            )}
            <button type="button" className="tos-link" onClick={() => signOut()} disabled={pending}>
                Sign out
            </button>
        </div>
    );

    if (step === 'done' || step === 'enrolled') {
        return (
            <div className="login-container mfa-setup-container">
                <div className="login-container-card mfa-setup-card">
                    <h2>Two-factor authentication is on</h2>
                    <p className="mfa-setup-copy">
                        {step === 'enrolled'
                            ? 'Two-factor authentication is already enabled on this account.'
                            : 'From now on, sign-in asks for a code from your authenticator app.'}
                    </p>
                    <button
                        type="button"
                        className="mfa-setup-submit"
                        onClick={() => navigate('/')}
                        disabled={pending}
                    >
                        Continue
                    </button>
                    {step === 'enrolled' && (
                        <div className="mfa-setup-footer">
                            <button type="button" className="tos-link" onClick={() => signOut()}>
                                Sign out
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (step === 'scan') {
        return (
            <div className="login-container mfa-setup-container">
                <div className="login-container-card mfa-setup-card">
                    <h2>Scan the QR code</h2>
                    <p className="mfa-setup-copy">
                        Open your authenticator app (Google Authenticator, Microsoft Authenticator, Authy, or 1Password)
                        and scan this code.
                    </p>
                    <div className="mfa-setup-qr">
                        <QRCodeSVG value={qrUrl} size={180} />
                    </div>
                    <p className="mfa-setup-key-label">Can't scan? Enter this key</p>
                    <code className="mfa-setup-key">{secret?.secretKey}</code>
                    {errorBlock}
                    <form onSubmit={handleVerify}>
                        <div>
                            <label htmlFor="mfa-code">Six-digit code</label>
                            <input
                                id="mfa-code"
                                type="text"
                                className="mfa-setup-code"
                                value={code}
                                onChange={onCodeChange}
                                required
                                minLength={6}
                                maxLength={6}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="one-time-code"
                                autoFocus
                                placeholder="123456"
                            />
                        </div>
                        <button type="submit" className="mfa-setup-submit" disabled={pending}>
                            Verify
                        </button>
                    </form>
                    {footer}
                </div>
            </div>
        );
    }

    return (
        <div className="login-container mfa-setup-container">
            <div className="login-container-card mfa-setup-card">
                <h2>Set up two-factor authentication</h2>
                <p className="mfa-setup-copy">
                    {required
                        ? 'Your role requires two-factor authentication.'
                        : 'Add a second factor to your account.'}
                </p>
                <p className="mfa-setup-copy">Confirm your password to start.</p>
                {errorBlock}
                <form onSubmit={handlePassword}>
                    <div>
                        <label htmlFor="mfa-password">Password</label>
                        <input
                            id="mfa-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            autoComplete="current-password"
                            autoFocus
                        />
                    </div>
                    <button type="submit" className="mfa-setup-submit" disabled={pending}>
                        Continue
                    </button>
                </form>
                {footer}
            </div>
        </div>
    );
};
